import { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'reactstrap';
import DOMPurify from 'dompurify';
import { getChatbotPublicConfig, sendChatbotMessage } from './FormikHelpers/Api';
import {
  getOrCreateSessionId,
  parseMarkdown,
  buildUserMessage,
  buildBotMessage,
  buildTypingMessage,
  FIELD_STATUS_COLORS,
} from './FormikHelpers';

const PURIFY_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'ul', 'ol', 'li', 'a', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:)/i,
};
const safeHtml = (raw) => DOMPurify.sanitize(parseMarkdown(raw || ''), PURIFY_CONFIG);

const ChatWidget = ({ token }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [formData, setFormData] = useState({});
  const [activeFormId, setActiveFormId] = useState(null);
  const [unread, setUnread] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(getOrCreateSessionId());

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await getChatbotPublicConfig(token);
        setConfig(res.data);
      } catch { }
    })();
  }, [token]);

  useEffect(() => {
    if (config && !initialized) {
      setInitialized(true);
      sendIntent('greeting');
    }
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isOpen && messages.length > 0) {
      const last = messages[messages.length - 1];
      if (last?.from === 'bot' && last?.type !== 'typing') setUnread((n) => n + 1);
    }
    if (isOpen) setUnread(0);
  }, [messages, isOpen]);

  const primary = config?.primary_color || '#1D4ED8';
  const secondary = config?.secondary_color || config?.accent_color || '#3B82F6';
  const gradient = `linear-gradient(135deg, ${primary}, ${secondary})`;

  const sendIntent = useCallback(async (intent, payload = {}, branchData = null) => {
    if (!token) return;
    setLoading(true);
    const typingMsg = buildTypingMessage();
    setMessages((prev) => [...prev, typingMsg]);

    try {
      const requestBody = {
        token,
        session_id: sessionId.current,
        intent,
        payload,
      };

      if (branchData?.branch_variable && branchData?.branch_value) {
        requestBody.branch_variable = branchData.branch_variable;
        requestBody.branch_value = branchData.branch_value;
      }

      const res = await sendChatbotMessage(requestBody);
      const botMsg = buildBotMessage(res.data);
      setMessages((prev) => [...prev.filter((m) => m.id !== typingMsg.id), botMsg]);
      if (res.data.form_fields || res.data.type === 'form') setActiveFormId(botMsg.id);
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== typingMsg.id));
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleOptionClick = useCallback((option) => {
    const label = `${option.icon ? option.icon + ' ' : ''}${option.label}`;
    setMessages((prev) => [...prev, buildUserMessage(label)]);

    const branchData = (option.branch_variable && option.branch_value) ? {
      branch_variable: option.branch_variable,
      branch_value: option.branch_value,
    } : null;

    sendIntent(option.intent, {}, branchData);
  }, [sendIntent]);

  const handleSend = useCallback(() => {
    const text = inputVal.trim();
    if (!text || loading) return;
    setMessages((prev) => [...prev, buildUserMessage(text)]);
    setInputVal('');
    sendIntent('trigger_match', { message: text });
  }, [inputVal, loading, sendIntent]);

  const handleFormSubmit = useCallback((msgId, formFields, submitIntent) => {
    for (const f of formFields.filter((f) => f.required)) {
      if (!formData[f.name]?.toString().trim()) {
        alert(`"${f.label}" is required`);
        return;
      }
    }
    setActiveFormId(null);
    const intent = submitIntent
      || (formFields.some((f) => f.name === 'unit_number') ? 'suite_info_lookup' : 'lead_capture_submit');
    const summary = formFields
      .filter((f) => formData[f.name])
      .map((f) => `${f.label}: ${formData[f.name]}`)
      .join(' · ');
    setMessages((prev) => [...prev, buildUserMessage(summary || 'Submitted')]);
    sendIntent(intent, formData);
    setFormData({});
  }, [formData, sendIntent]);

  const renderFormField = (field, msgId) => {
    const val = formData[field.name] || '';
    const onChange = (e) => setFormData((d) => ({ ...d, [field.name]: e.target.value }));

    const sharedProps = {
      value: val,
      onChange,
      onFocus: (e) => (e.target.style.borderColor = primary),
      onBlur: (e) => (e.target.style.borderColor = ''),
    };

    return (
      <div key={field.name} className="cb-form__field">
        <label className="cb-form__label">
          {field.label}
          {field.required && <span className="cb-form__required">*</span>}
        </label>

        {field.type === 'textarea' ? (
          <textarea rows={3} className="cb-form__textarea" {...sharedProps} placeholder={field.placeholder || ''} />
        ) : field.type === 'select' ? (
          <select className="cb-form__input" value={val} onChange={onChange}>
            <option value="">Select…</option>
            {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : field.type === 'radio' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {(field.options || []).map((o) => (
              <label key={o} style={{
                display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer',
                padding: '4px 10px', borderRadius: 20,
                border: `1.5px solid ${val === o ? primary : '#E2E5EE'}`,
                background: val === o ? `${primary}15` : '#fff',
                color: val === o ? primary : '#334155',
                fontWeight: val === o ? 600 : 400,
              }}>
                <input type="radio" name={`${msgId}_${field.name}`} value={o} checked={val === o}
                  onChange={() => setFormData((d) => ({ ...d, [field.name]: o }))} style={{ display: 'none' }} />
                {o}
              </label>
            ))}
          </div>
        ) : field.type === 'checkbox' ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12 }}>
            <input type="checkbox" checked={!!val}
              onChange={(e) => setFormData((d) => ({ ...d, [field.name]: e.target.checked ? 'yes' : '' }))} />
            {field.placeholder || field.label}
          </label>
        ) : (
          <input type={field.type} className="cb-form__input" {...sharedProps} placeholder={field.placeholder || ''} />
        )}
      </div>
    );
  };

  const renderMessage = (msg) => {
    if (msg.from === 'user') {
      return (
        <div key={msg.id} className="cb-bubble cb-bubble--user" style={{ background: gradient }}>
          {msg.content}
        </div>
      );
    }

    if (msg.type === 'typing') {
      return (
        <div key={msg.id} className="cb-typing">
          <div className="cb-typing__dot" />
          <div className="cb-typing__dot" />
          <div className="cb-typing__dot" />
        </div>
      );
    }

    return (
      <div key={msg.id} style={{ alignSelf: 'flex-start', maxWidth: '92%', width: '100%' }}>

        {msg.pre_content && (
          <div className="cb-bubble cb-bubble--bot"
            dangerouslySetInnerHTML={{ __html: safeHtml(msg.pre_content) }} />
        )}

        {msg.content && msg.type !== 'card' && (
          <div className="cb-bubble cb-bubble--bot"
            dangerouslySetInnerHTML={{ __html: safeHtml(msg.content) }} />
        )}

        {msg.options?.length > 0 && (
          <div className="cb-options">
            {msg.options.map((opt, idx) => (
              <button
                key={opt.intent || idx}
                className="cb-option-btn"
                style={{ borderColor: primary, color: primary }}
                onClick={() => handleOptionClick(opt)}
                onMouseEnter={(e) => { e.currentTarget.style.background = primary; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = primary; }}
              >
                {opt.icon ? <span style={{ marginRight: 4 }}>{opt.icon}</span> : null}{opt.label}
              </button>
            ))}
          </div>
        )}

        {msg.type === 'card' && msg.fields && (
          <div className="cb-card">
            {msg.content && (
              <div className="cb-card__header" style={{ background: `${primary}15`, color: primary }}>
                <span dangerouslySetInnerHTML={{ __html: safeHtml(msg.content) }} />
              </div>
            )}
            {msg.fields.map((f, idx) => {
              const st = FIELD_STATUS_COLORS[f.status] || FIELD_STATUS_COLORS.neutral;
              return (
                <div key={f.label || idx} className="cb-card__row">
                  <span className="cb-card__label">{f.label}</span>
                  <span className={`cb-card__value cb-card__value--${f.status || 'neutral'}`}
                    style={{ color: st.color, background: st.background }}>
                    {f.value}
                  </span>
                </div>
              );
            })}
            {msg.cta && (
              <a href={msg.cta.url} target="_blank" rel="noreferrer"
                className="cb-card__cta" style={{ background: gradient }}>
                {msg.cta.label} →
              </a>
            )}
          </div>
        )}

        {msg.type === 'link' && msg.cta && (
          <a href={msg.cta.url} target="_blank" rel="noreferrer"
            className="cb-link-cta" style={{ background: gradient }}>
            {msg.cta.label} →
          </a>
        )}

        {msg.type === 'form' && msg.form_fields && activeFormId === msg.id && (
          <div className="cb-form">
            {[...(msg.form_fields || [])]
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((field) => renderFormField(field, msg.id))}
            <button
              className="cb-form__submit"
              style={{ background: gradient }}
              onClick={() => handleFormSubmit(msg.id, msg.form_fields, msg.submit_intent)}
            >
              Submit →
            </button>
          </div>
        )}
      </div>
    );
  };

  const launcherIcon = config?.launcher_icon_url
    ? null
    : (config?.launcher_icon || '🏠');

  return (
    <>
      <style>{`
        .cb-option-btn { border-color: ${primary}; color: ${primary}; }
        .cb-option-btn:hover { background: ${primary} !important; color: #fff !important; }
        .cb-send-btn { background: ${gradient}; }
        .cb-text-input:focus { border-color: ${primary}; box-shadow: 0 0 0 3px ${primary}22; }
      `}</style>
      <button
        className="cb-launcher"
        style={{ background: gradient, boxShadow: `0 4px 24px ${primary}55` }}
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Open chat"
      >
        {unread > 0 && !isOpen && (
          <span className="cb-launcher__badge">{unread > 9 ? '9+' : unread}</span>
        )}
        <span className="cb-launcher__icon">
          {isOpen
            ? (config?.closing_icon_url
              ? <img src={config.closing_icon_url} alt="" style={{ width: 50, height: 50, objectFit: 'contain' }} />
              : (config?.closing_icon || '✕'))
            : (config?.launcher_icon_url
              ? <img src={config.launcher_icon_url} alt="" style={{ width: 50, height: 50, objectFit: 'contain' }} />
              : launcherIcon)
          }
        </span>
      </button>

      {isOpen && (
        <div className="cb-window">

          <div className="cb-header" style={{ background: gradient }}>
            <div className="cb-header__avatar">
              {config?.logo_icon_url
                ? <img src={config.logo_icon_url} alt="bot" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : (config?.logo_icon || '🏠')}
            </div>
            <div className="cb-header__meta">
              <p className="cb-header__name">{config?.bot_name || 'Assistant'}</p>
              <p className="cb-header__status">
                Online · Typically replies instantly
              </p>
            </div>
            <button className="cb-header__close" onClick={() => setIsOpen(false)}>✕</button>
          </div>

          <div className="cb-messages">
            {messages.map(renderMessage)}
            <div ref={bottomRef} />
          </div>

          <div className="cb-input-row">
            <input
              className="cb-text-input"
              placeholder={config?.placeholder_text || 'Type a message...'}
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            />
            <button
              className="cb-send-btn"
              style={{ background: gradient }}
              onClick={handleSend}
              disabled={loading}
            >
              {loading
                ? <Spinner size="sm" style={{ width: 14, height: 14 }} />
                : '➤'}
            </button>
          </div>

        </div>
      )}
    </>
  );
};

export { ChatWidget };